import _padStart from 'lodash/padStart';
import {
  AmExCardIcon,
  CreditCardIcon,
  DiscoverCardIcon,
  MasterCardIcon,
  NeimanMarcusCardIcon,
  BergdorfGoodmanCardIcon,
  VisaCardIcon,
  CashPaymentIcon,
  CheckPaymentIcon,
  GiftPaymentIcon,
  DinersClubCardIcon,
  JCBCardIcon,
  AffirmPaymentIcon,
} from 'sharedComponents/Icons';
import { getLast4Digits } from './commonUtils';

const PLC_CARD_TYPE = 'PLC';

const CFA_CARD_TYPES = {
  VIC: 'Visa',
  MCC: 'MasterCard',
  AXC: 'American Express',
  DCC: "Diners' Club",
  NVC: 'Discover',
  JBC: 'JCB',
  NM: 'Neiman Marcus',
  BG: 'Bergdorf Goodman',
  PPC: 'PayPal',
  CUP: 'China UnionPay',
};

const SHORT_CARD_TYPES = {
  VISA: 'VIC',
  MASTERCARD: 'MCC',
  'AMERICAN EXPRESS': 'AXC',
  "DINERS' CLUB": 'DCC',
  DISCOVER: 'NVC',
  JCB: 'JBC',
  'NEIMAN MARCUS': PLC_CARD_TYPE,
  'BERGDORF GOODMAN': PLC_CARD_TYPE,
  PAYPAL: 'PPC',
  'CHINA UNIONPAY': 'CUP',
};

export const getCardIconByCardType = (cardType = '') => {
  if (cardType.toUpperCase().startsWith('GC')) {
    return GiftPaymentIcon;
  }
  if (cardType.toUpperCase().startsWith('NM')) {
    return NeimanMarcusCardIcon;
  }
  if (cardType.toUpperCase().startsWith('BG')) {
    return BergdorfGoodmanCardIcon;
  }

  switch (cardType.toUpperCase()) {
    case 'VISA':
    case 'VIC':
      return VisaCardIcon;

    case 'MASTERCARD':
    case 'MCC':
    case 'MC':
      return MasterCardIcon;

    case 'CHG':
    case 'PLC':
    case 'PLCC':
    case CFA_CARD_TYPES.NM.toUpperCase():
      return NeimanMarcusCardIcon;

    case CFA_CARD_TYPES.JBC:
    case SHORT_CARD_TYPES.JCB:
      return JCBCardIcon;

    case CFA_CARD_TYPES.DCC.toUpperCase():
    case SHORT_CARD_TYPES["DINNERS' CLUB"]:
    case 'DCC':
      return DinersClubCardIcon;

    case CFA_CARD_TYPES.BG.toUpperCase():
      return BergdorfGoodmanCardIcon;

    case 'DISCOVER':
    case 'NVC':
    case 'DISC':
      return DiscoverCardIcon;

    case 'AMERICAN EXPRESS':
    case 'AXC':
    case 'AMEX':
      return AmExCardIcon;

    case 'CASH':
      return CashPaymentIcon;

    case 'CHECK':
      return CheckPaymentIcon;

    case 'NEX':
      return GiftPaymentIcon;

    case 'AFFIRM':
      return AffirmPaymentIcon;

    default:
      return CreditCardIcon;
  }
};

export const convertAurusCardType = (aurusCardType = '') => CFA_CARD_TYPES[aurusCardType] || aurusCardType;

export const maskCardNumber = (cardNumber) => {
  if (!cardNumber?.length) return cardNumber;

  const lengthOfVisibleDigits = 4;
  const lengthOfMaskedDigits = cardNumber.length - lengthOfVisibleDigits;
  const last4digits = cardNumber.slice(lengthOfMaskedDigits);

  return _padStart(last4digits, lengthOfMaskedDigits, 'x');
};

export const convertToShortType = (networkType = '') => {
  const upperCaseType = networkType.toUpperCase();

  /**
   * Fix invalid payment payload bug (OSP-14364)
   * For some time when added new PLC wrong card type was set(subCardType instead cardType)
   * Here we fix cardType for already added cards
   */
  if (upperCaseType.startsWith('NM') || upperCaseType.startsWith('BG')) {
    return PLC_CARD_TYPE;
  }

  return SHORT_CARD_TYPES[upperCaseType] || networkType;
};

export const getUnexpiredCards = (assets) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!assets) return [];

  return assets.filter((card) => {
    if (!card.expDate) {
      return true;
    }

    return new Date(card.expDate.replace(/-/g, '/')) >= today;
  });
};

export const getNonZeroDollarGiftCards = (assets = []) => assets.filter((card) => Number(card.giftCardBalance));

/**
 * @typedef {import('../modules/cart/types').GiftCardDTO} GiftCardDTO
 *
 * Returns title for gift card
 * @param {GiftCardDTO} card
 */
export const displayGiftCard = (card) => {
  const cardLabel = card.giftCardLabel || 'Gift Card';
  const cardLastDigits = getLast4Digits({ maskedNumber: card.giftCardNumber });

  return `${cardLabel} ${cardLastDigits}`;
};
